import { Controller } from "@hotwired/stimulus"

const ACCENT_FROM = "áàäâéèëêíìïîóòöôúùüûñ"
const ACCENT_TO = "aaaaaeeeeeiiiiioooouuuun"

export default class extends Controller {
  static targets = ["trigger", "triggerLabel", "panel", "searchInput", "option", "checkbox", "emptyState", "optionsList", "allCheckbox", "selectAllCheckbox"]
  static values = {
    placeholder: String,
    allOptionLabel: String,
    selectedCountTemplate: String
  }

  connect() {
    this.open = false
    this.boundOutside = this.onDocumentPointerDown.bind(this)
    this.boundEscape = this.onEscape.bind(this)
    document.addEventListener("pointerdown", this.boundOutside, true)
    document.addEventListener("keydown", this.boundEscape)
    this.updateTriggerLabel()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.boundOutside, true)
    document.removeEventListener("keydown", this.boundEscape)
  }

  togglePanel(event) {
    event.preventDefault()
    event.stopPropagation()
    this.open ? this.closePanel() : this.openPanel()
  }

  openPanel() {
    this.open = true
    this.element.classList.add("z-50")
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus()
    }
  }

  closePanel() {
    this.open = false
    this.element.classList.remove("z-50")
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  onDocumentPointerDown(event) {
    if (!this.open || this.element.contains(event.target)) return
    this.closePanel()
  }

  onEscape(event) {
    if (event.key === "Escape" && this.open) {
      this.closePanel()
      this.triggerTarget.focus()
    }
  }

  filter() {
    const query = this.normalizeForSearch(this.searchInputTarget.value.trim())
    let visibleCount = 0

    this.optionTargets.forEach((option) => {
      const label = this.normalizeForSearch(option.dataset.filterableMultiSelectSearchLabel || "")
      const matches = query.length === 0 || label.includes(query)
      option.classList.toggle("hidden", !matches)
      if (matches) visibleCount += 1
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", visibleCount > 0)
    }
    this.syncSelectAllCheckbox()
  }

  updateTriggerLabel() {
    const checked = this.checkboxTargets.filter((checkbox) => checkbox.checked)
    let label = this.hasAllOptionLabelValue && this.allOptionLabelValue
      ? this.allOptionLabelValue
      : this.placeholderValue

    if (checked.length === 1) {
      label = checked[0].dataset.filterableMultiSelectOptionLabel || label
    } else if (checked.length > 1) {
      label = this.selectedCountTemplateValue.replace("__COUNT__", String(checked.length))
    }

    this.triggerLabelTarget.textContent = label
    this.syncAllCheckbox(checked.length === 0)
    this.syncSelectAllCheckbox()
  }

  // Checks (or clears) every option the search currently shows.
  toggleSelectAll() {
    const checked = this.selectAllCheckboxTarget.checked
    this.visibleCheckboxes().forEach((checkbox) => {
      checkbox.checked = checked
    })
    this.updateTriggerLabel()
  }

  syncSelectAllCheckbox() {
    if (!this.hasSelectAllCheckboxTarget) return

    const visible = this.visibleCheckboxes()
    this.selectAllCheckboxTarget.checked = visible.length > 0 && visible.every((checkbox) => checkbox.checked)
  }

  visibleCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => !checkbox.closest("li").classList.contains("hidden"))
  }

  toggleAll() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    if (this.hasAllCheckboxTarget) {
      this.allCheckboxTarget.checked = true
    }
    this.updateTriggerLabel()
  }

  syncAllCheckbox(noneSelected) {
    if (!this.hasAllCheckboxTarget) return

    this.allCheckboxTarget.checked = noneSelected
  }

  normalizeForSearch(value) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
  }
}
